import { Box, Button, Typography, Checkbox, FormControlLabel, CircularProgress, useMediaQuery, Grid } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import styles from "../../assets/styles/form.module.css";
import { useState } from "react";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import getTheme from "../../theme/theme";
import { PinnacleThemes } from "../../theme/types";
import { login } from "../../api/services/auth";
import { PREBOARD_STORAGE_KEY, E_ENTER_EMAIL_ADDRESS, E_ENTER_PASSWORD, LOGIN_SUCCESS_MESSAGE, PRIMARY_COLOR, PREBOARD_AUTH_BANNER_DESKTOP_LATEST, PREBOARD_AUTH_BANNER_MOBILE_LATEST, PINNACLE_LOGO_URL } from "../../utils/constants";
import PasswordAdornment from "../../components/common/PasswordAdornment/PasswordAdornment";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { handleApiError } from "../../utils/errorHandler";

export default function LoginPage() {
    const theme = getTheme(PinnacleThemes.BASE_THEME);
    const navigate = useNavigate();

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isRemember, setIsRemember] = useState<boolean>(true);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<string | null>(null);

    const isEmailValid = email.length > 0; // EMAIL_REGEX.test(email);
    const isPasswordValid = password.length > 0;

    const handleTogglePassword = () => setShowPassword((prev) => !prev);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (!emailTouched) setEmailTouched(true);
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (!passwordTouched) setPasswordTouched(true);
        if (loginError) setLoginError(null);
    };

    const handleRememberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsRemember(e.target.checked);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setEmailTouched(true);
        setPasswordTouched(true);

        // Early return if invalid, for clarity
        if (!isEmailValid || !isPasswordValid) {
            return;
        }

        setFormDisabled(true);

        try {
            // delete the key from local storage and session storage
            localStorage.removeItem(PREBOARD_STORAGE_KEY);
            sessionStorage.removeItem(PREBOARD_STORAGE_KEY);

            const loginResponse = await login({ email, password });
            const token = loginResponse.data?.token;
            const user = loginResponse.data?.user;

            if (!token || !user) {
                throw new Error("Invalid login response.");
            }

            if (isRemember) {
                localStorage.setItem(PREBOARD_STORAGE_KEY, token);
            } else {
                sessionStorage.setItem(PREBOARD_STORAGE_KEY, token);
            }

            pushNotification({
                title: LOGIN_SUCCESS_MESSAGE,
                type: NotificationTypes.Success,
            });
            if (!user.isExclusive && !user.isProfileSet) {
                navigate("/setup");
            } else {
                navigate("/");
            }
        } catch (error) {
            const { errorMessage } = handleApiError(error);
            setLoginError(errorMessage);
        } finally {
            setFormDisabled(false);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', flexDirection: { xs: 'column', md: 'row' } }}>
            {/* Left image panel */}
            <Grid
                size={{
                    xs: 12,
                    md: 5
                }}
                sx={{
                    height: { xs: '200px', md: 'auto' },
                    overflow: "hidden",
                    backgroundImage: {
                        xs: `url(${PREBOARD_AUTH_BANNER_MOBILE_LATEST})`,
                        md: `url(${PREBOARD_AUTH_BANNER_DESKTOP_LATEST})`
                    },
                    backgroundRepeat: "no-repeat",
                    backgroundSize: { xs: 'cover', md: 'contain' },
                    backgroundPosition: "left top",
                    backgroundColor: "#fff",
                }}
            />

            {/* Right form panel */}
            <Grid
                size={{
                    xs: 12,
                    md: 7
                }}
                sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    alignItems: 'center',
                    placeSelf: 'center',
                    overflowY: 'auto',
                    p: { xs: 2, md: 5 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: 'center', md: 'flex-start' },
                        alignItems: { xs: 'center', md: 'flex-start' },
                        width: '100%',
                        minHeight: '100%',
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: "450px",
                            minWidth: "300px",
                            width: "90%",
                            borderRadius: "5px",
                            boxSizing: "border-box",
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        <img
                            src={PINNACLE_LOGO_URL}
                            alt='pinnacle-logo'
                            style={{
                                width: "300px",
                                height: "auto",
                                alignSelf: "center",
                            }}
                        />
                        <Typography variant="h3" sx={{ alignSelf: "center", fontWeight: 600 }}>
                            Exam Portal
                        </Typography>
                        <form onSubmit={handleSubmit} noValidate>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <PinnacleTextField
                                    error={emailTouched && !isEmailValid}
                                    color={emailTouched && (!isEmailValid || !!loginError) ? 'error' : 'primary'}
                                    helperText={emailTouched && !isEmailValid ? E_ENTER_EMAIL_ADDRESS : ''}
                                    id="email"
                                    autoFocus
                                    label="Email Address"
                                    variant="outlined"
                                    onChange={handleEmailChange}
                                    onBlur={() => setEmailTouched(true)}
                                    value={email}
                                    autoComplete="email"
                                    slotProps={{
                                        htmlInput: {
                                            sx: {
                                                'aria-label': 'email'
                                            }
                                        }
                                    }}
                                    disabled={formDisabled}
                                />
                                <PinnacleTextField
                                    error={passwordTouched && (!isPasswordValid || !!loginError)}
                                    color={passwordTouched && (!isPasswordValid || !!loginError) ? 'error' : 'primary'}
                                    helperText={
                                        passwordTouched && !isPasswordValid
                                            ? E_ENTER_PASSWORD
                                            : loginError
                                    }
                                    id="password"
                                    label="Password"
                                    variant="outlined"
                                    onChange={handlePasswordChange}
                                    onBlur={() => setPasswordTouched(true)}
                                    value={password}
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="current-password"
                                    disabled={formDisabled}
                                    slotProps={{
                                        input: {
                                            endAdornment: <PasswordAdornment show={showPassword} toggleShow={handleTogglePassword} />,
                                        },
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isRemember}
                                                onChange={handleRememberChange}
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    fontSize: '14px',
                                                    color: PRIMARY_COLOR,
                                                    '&.Mui-checked': {
                                                        color: PRIMARY_COLOR,
                                                    },
                                                }}
                                            />
                                        }
                                        label="Remember Me"
                                        sx={{
                                            color: "#818181"
                                        }}
                                    />
                                </Box>
                                <Button
                                    variant="text"
                                    sx={{ color: "#5840BA", textTransform: "none", fontSize: 14, fontWeight: 500, minWidth: "auto" }}
                                    onClick={() => navigate("/forgot-password")}
                                >
                                    Forgot password?
                                </Button>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    type="submit"
                                    disabled={!isEmailValid || !isPasswordValid || formDisabled}
                                    sx={{ mb: 1.5 }}
                                >
                                    {formDisabled ? (
                                        <CircularProgress size={24} sx={{ color: "#90caf9" }} />
                                    ) : (
                                        "SIGN IN"
                                    )}
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "left",
                                }}
                            >
                                <Typography variant="body2" sx={{ color: "#818181" }}>
                                    Not a Pinnacle student?{" "}
                                    <Link
                                        to="/register"
                                        style={{
                                            color: "#5840BA",
                                            textDecoration: "none",
                                            fontWeight: 500
                                        }}
                                    >
                                        Register here
                                    </Link>
                                </Typography>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
}